import {
  AutoPayEnrollRequest,
  PolicyTypeCd,
  WalletPayment,
} from '@aaa-ncnu-ie/mypolicy-api-billing';
import { Box } from '@mui/material';
import { parse, format, isValid } from 'date-fns';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useBillsQuery } from '../../../api/getBills';
import PaymentTypeButton from '../../../components/PaymentTypeButton';
import Button from '../../../components/styledComponents/Button';
import {
  EValueStatus,
  PaymentType,
  StatusType,
} from '../../../constants/enums';
import { AutoPayMessages } from '../../../constants/paymentMessages';
import { useSnackbarsContext } from '../../../store/SnackbarProvider';
import { usePaymentsContext } from '../../../store/payments';
import AccountPaymentTypeForm from '../../Payment/AccountPaymentTypeForm';
import CardPaymentTypeForm from '../../Payment/CardPaymentTypeForm';
import PaymentPage from '../../Payment/components/PaymentPage';
import { useAutoPayEnrollMutation } from '../utils';

const getPaymentType = (walletAccount: WalletPayment): string => {
  if (walletAccount) {
    if ('card' in walletAccount) return PaymentType.CREDIT;
    if (walletAccount?.account?.type === 'CHKG') return PaymentType.CHECKING;
    if (walletAccount?.account?.type === 'SVG') return PaymentType.SAVINGS;
  }

  return '';
};

const AutoPayPaymentMethodActions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = location.state?.edit;
  const isBulkEnroll = location.state?.bulk;
  const walletAccount: WalletPayment = location.state?.walletAccount;
  const { data: billsData } = useBillsQuery();
  const isEValue =
    [EValueStatus.ACTIVE, EValueStatus.PENDING].includes(
      location.state?.eValue?.toUpperCase() as EValueStatus
    ) && !billsData?.payments.find((payment) => payment.riskState === 'MD');
  const { state } = usePaymentsContext();
  const [selectedPaymentType, setSelectedPaymentType] = useState(
    getPaymentType(walletAccount)
  );
  const { enqueueSnackbar } = useSnackbarsContext();

  const { enroll } = useAutoPayEnrollMutation(isBulkEnroll, isEdit);

  const getContent = () => {
    let extra = { formValues: {}, text: '' };
    switch (selectedPaymentType) {
      case PaymentType.CREDIT: {
        const card = walletAccount?.card;
        if (card) {
          const formattedExpirationDate = format(
            parse(card.expirationDate, 'MM/dd/yyyy', new Date()),
            'MM/yy'
          );
          extra = {
            formValues: {
              name: card.holderName,
              cardNumber: `${'*'.repeat(12)}${card.number}`,
              expirationDate: formattedExpirationDate,
              zipCode: card.zipCode,
              nickname: walletAccount?.shortName ?? '',
            },
            text: `${card?.type} ****${card?.number}`,
          };
        }
        return { title: 'debit/credit card', extra };
      }
      case PaymentType.CHECKING: {
        const accountInfo = walletAccount?.account;
        if (accountInfo) {
          extra = {
            formValues: {
              name: accountInfo?.holderName,
              routingNumber: accountInfo?.institution?.routingNumber,
              accountNumber: `*******${accountInfo?.number}`,
              nickname: walletAccount?.shortName ?? '',
            },
            text: `checking ****${accountInfo.number}`,
          };
        }
        return { title: 'checking account', extra };
      }
      case PaymentType.SAVINGS: {
        const accountInfo = walletAccount?.account;
        if (accountInfo) {
          extra = {
            formValues: {
              name: accountInfo?.holderName,
              routingNumber: accountInfo?.institution?.routingNumber,
              accountNumber: `*******${accountInfo?.number}`,
              nickname: walletAccount?.shortName ?? '',
            },
            text: `savings ****${accountInfo.number}`,
          };
        }
        return { title: 'savings account', extra };
      }
      default:
        return { title: 'payment method', extra: { text: '' } };
    }
  };

  const handleFormSubmit = async (submitResponse?: any) => {
    const { paymentAccountToken, paymentAcctFopType } = submitResponse;

    try {
      let policies: AutoPayEnrollRequest[] = [];

      if (isBulkEnroll) {
        policies =
          billsData?.payments
            ?.filter(
              (payment) => payment.autoPay.eligible && !payment.autoPay.enrolled
            )
            ?.map((payment) => ({
              policyNumber: payment.policyNumber,
              policyInfo: { productType: payment.policyTypeCd as PolicyTypeCd },
              paymentItem: {
                paymentMethod: paymentAcctFopType,
                paymentAccountToken,
              },
            })) ?? [];
      } else {
        const { selectedBill } = state;
        policies = [
          {
            policyNumber: selectedBill.policyNumber,
            policyInfo: {
              productType: selectedBill.policyTypeCd as PolicyTypeCd,
            },
            paymentItem: {
              paymentMethod: paymentAcctFopType,
              paymentAccountToken,
            },
          },
        ];
      }
      await enroll.mutateAsync({
        policies,
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar({
        message: AutoPayMessages.FAIL,
        variant: StatusType.ERROR,
        autoHideDuration: 10000,
      });
    }

    navigate('/autopay');
  };

  const renderContent = (): React.ReactElement => {
    switch (selectedPaymentType) {
      case PaymentType.CREDIT:
        return (
          <CardPaymentTypeForm
            onSubmit={handleFormSubmit}
            buttonText={isEdit ? 'Save' : 'Done'}
            cancelAddPaymentMethod={() => {
              if (walletAccount) {
                return navigate(-1);
              }
              return setSelectedPaymentType('');
            }}
            paymentAccountToken={walletAccount?.paymentAccountToken}
            showTermsAndConditions
            isEValue={isEValue}
            isAutoPay
            isLoading={enroll.isPending}
            {...(walletAccount ? getContent().extra : {})}
          />
        );
      case PaymentType.CHECKING:
      case PaymentType.SAVINGS:
        return (
          <AccountPaymentTypeForm
            accountType={selectedPaymentType}
            onSubmit={handleFormSubmit}
            buttonText={isEdit ? 'Save' : 'Done'}
            cancelAddPaymentMethod={() => {
              if (walletAccount) {
                return navigate(-1);
              }
              return setSelectedPaymentType('');
            }}
            paymentAccountToken={walletAccount?.paymentAccountToken}
            showTermsAndConditions
            isAutoPay
            isLoading={enroll.isPending}
            {...(walletAccount ? getContent().extra : {})}
          />
        );
      default:
        return (
          <>
            <PaymentTypeButton
              data-testid="creditTypeButton"
              onClick={() => setSelectedPaymentType(PaymentType.CREDIT)}
            >
              Debit/credit card
            </PaymentTypeButton>
            <PaymentTypeButton
              data-testid="checkingTypeButton"
              onClick={() => setSelectedPaymentType(PaymentType.CHECKING)}
            >
              Checking account
            </PaymentTypeButton>
            <PaymentTypeButton
              data-testid="savingsTypeButton"
              onClick={() => setSelectedPaymentType(PaymentType.SAVINGS)}
            >
              Savings account
            </PaymentTypeButton>
            <Box
              sx={{
                '&.MuiBox-root': {
                  mt: { mobile: 2, tablet: 3 },
                  display: 'flex',
                  flexDirection: { mobile: 'column', tablet: 'row' },
                  justifyContent: 'flex-end',
                },
              }}
            >
              <Button
                variant="outlined"
                color="inherit"
                data-testid="addPaymentMethodSelectCancel"
                sx={{
                  borderWidth: 2,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: 18,
                  color: 'text.secondary',
                  px: 4,
                  py: 0.5,
                }}
                disableRipple
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
            </Box>
          </>
        );
    }
  };

  return (
    <>
      <PaymentPage
        pageTitle={
          walletAccount
            ? `Edit ${getContent().title}`
            : `Add ${getContent().title}`
        }
      >
        {renderContent()}
      </PaymentPage>
    </>
  );
};

export default AutoPayPaymentMethodActions;
